import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col, Alert } from "reactstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { listVariant } from "../../pages/Plan-Subscription/_requests";

interface UpgradeDowngradeModalProps {
  isOpen: boolean;
  toggle: () => void;
  subscription: any;
  onUpgradeDowngrade: (data: any) => Promise<void>;
}

const UpgradeDowngradeModal: React.FC<UpgradeDowngradeModalProps> = ({
  isOpen,
  toggle,
  subscription,
  onUpgradeDowngrade,
}) => {
  const [variants, setVariants] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<any>({
    actionType: "upgrade",
    new_variant_id: "",
    new_variant_value: "",
    start_date: "",
    end_date: "",
    validity: "",
    validity_type: "",
    remaining_days: 0,
    reason: "",
    calculated_remaining_days: 0,
    current_variant_value: 0,
    current_variant_id: "",
    current_variant_name: "",
    per_day_amount: 0,
    new_end_date: "",
    new_start_date: ""
  });

  useEffect(() => {
    const fetchVariants = async () => {
      try {
        const response = await listVariant();
        setVariants(response.data);
      } catch (error) {
        console.error("Error fetching variants:", error);
      }
    };
    fetchVariants();
  }, []);

  useEffect(() => {
    if (subscription && variants.length > 0) {
      // Access the correct data path based on your actual data structure
      const currentDetails = subscription.details?.[0] || {};
      const currentVariantValue = parseFloat(currentDetails.variant_value) || 0;
      const currentVariantId = currentDetails.variant_id || "";
      console.log("subscription userupgradedowngrade modal page", subscription)
      // Find the current variant in the variants list
      const currentVariant = variants.find(v => v.id === currentVariantId);

      setFormData({
        actionType: "upgrade",
        new_variant_id: currentVariantId,
        new_variant_value: currentDetails.variant_value || "",
        start_date: subscription.start_date || "",
        end_date: subscription.end_date || "",
        validity: currentDetails.validity || "",
        validity_type: currentDetails.validity_type || "",
        remaining_days: subscription.remaining_days_plan || 0,
        per_day_amount: currentDetails.per_day_amount || 0,
        gross_amount: subscription.gross_amount || 0,
        actual_amount: currentDetails.actual_amount || 0,
        reason: "",
        calculated_remaining_days: subscription.remaining_days_plan || 0,
        current_variant_value: currentVariantValue,
        current_variant_id: currentVariantId,
        current_variant_name: currentVariant?.name || "",
        new_end_date: subscription.end_date || "",
        new_start_date: subscription.start_date || ""
      });
    }
  }, [subscription, variants]);

  useEffect(() => {
    listVariant().then((response: any) => {
      setVariants(response.data);
    });
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required field validation
    if (!formData.new_variant_id) {
      newErrors.new_variant_id = "Variant type is required";
    }

    if (!formData.new_variant_value) {
      newErrors.new_variant_value = "Variant value is required";
    } else if (formData.actionType == "upgrade" && parseFloat(formData.new_variant_value) <= formData.current_variant_value) {
      newErrors.new_variant_value = `Value must be greater than current value (${formData.current_variant_value})`;
    } else if (formData.actionType == "downgrade" && parseFloat(formData.new_variant_value) >= formData.current_variant_value) {
      newErrors.new_variant_value = `Value must be less than current value (${formData.current_variant_value})`;
    }

    if (!formData.reason) {
      newErrors.reason = "Reason is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateNewDates = (currentEndDate: string, remainingDays: number, actionType: string) => {
    const currentEnd = moment(currentEndDate);
    let newEndDate;

    if (actionType === "upgrade") {
      // For upgrade: end date decreases (earlier end date)
      newEndDate = currentEnd.subtract(remainingDays, 'days').format('YYYY-MM-DD');
    } else {
      // For downgrade: end date increases (later end date)
      newEndDate = currentEnd.add(remainingDays, 'days').format('YYYY-MM-DD');
    }

    return newEndDate;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData((prev: any) => {
      const updatedData = {
        ...prev,
        [name]: value
      };

      // Recalculate remaining days when variant value or action type changes
      if (name === "new_variant_value" || name === "actionType" || name === "new_variant_id") {
        const currentValue = parseFloat(prev.current_variant_value);
        let newValue;

        if (name === "new_variant_id") {
          const selectedVariant = variants.find(v => v.id === value);
          newValue = selectedVariant?.value || prev.new_variant_value;
          updatedData.new_variant_value = selectedVariant?.value || "";
        } else if (name === "new_variant_value") {
          newValue = value;
        } else {
          newValue = updatedData.new_variant_value;
        }

        newValue = parseFloat(newValue);

        if (!isNaN(currentValue) && !isNaN(newValue) && currentValue > 0 && newValue > 0) {
          if (updatedData.actionType === "upgrade") {
            // For upgrade: remaining days decrease proportionally
            updatedData.calculated_remaining_days = Math.round(
              (prev.remaining_days * currentValue) / newValue
            );
          } else {
            // For downgrade: remaining days increase proportionally
            updatedData.calculated_remaining_days = Math.round(
              (prev.remaining_days * currentValue) / newValue
            );
          }

          // Calculate new end date based on adjusted remaining days
          const daysDifference = prev.remaining_days - updatedData.calculated_remaining_days;
          updatedData.new_end_date = calculateNewDates(
            prev.end_date,
            Math.abs(daysDifference),
            updatedData.actionType
          );

          // Calculate per day amount
          if (updatedData.calculated_remaining_days > 0) {
            updatedData.per_day_amount = (newValue / updatedData.calculated_remaining_days).toFixed(2);
          }
        }
      }

      return updatedData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please Enter required field values");
      return;
    }

    try {
      const payload = {
        is_upgrade_downgrade: true,
        user_subscription_id: subscription.id,
        subscription_id: subscription.subscription_id,
        user_id: subscription.user_id,
        action_type: formData.actionType,
        new_variant_id: formData.new_variant_id,
        new_variant_value: formData.new_variant_value,
        adjusted_remaining_days: formData.calculated_remaining_days,
        new_end_date: formData.new_end_date,
        reason: formData.reason
      };
      console.log("upgrade downgrade API Request page ", payload)
      await onUpgradeDowngrade(payload);
      toast.success(`Subscription ${formData.actionType} successful!`);
      toggle();
      setErrors({});
    } catch (error) {
      toast.error(`Failed to ${formData.actionType} subscription`);
      console.error(error);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Subscription Management
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Action Type</Label>
                <Input
                  type="select"
                  name="actionType"
                  value={formData.actionType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="upgrade">Upgrade</option>
                  <option value="downgrade">Downgrade</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Current Plan</Label>
                <Input
                  type="text"
                  value={subscription?.subscription?.name || ""}
                  readOnly
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={6}>
              <FormGroup>
                <Label>Current Variant</Label>
                <Input
                  type="text"
                  value={`${subscription?.details?.[0]?.variant_value || ''} ${subscription?.details?.[0]?.variant?.name || ''}`}
                  readOnly
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>New Variant Type *</Label>
                <Input
                  type="select"
                  name="new_variant_id"
                  value={formData.new_variant_id}
                  onChange={handleInputChange}
                  invalid={!!errors.new_variant_id}
                >
                  <option value="">Select Variant</option>
                  {variants.map(variant => (
                    <option key={variant.id} value={variant.id}>
                      {variant.name}
                    </option>
                  ))}
                </Input>
                {errors.new_variant_id && <div className="text-danger small mt-1">{errors.new_variant_id}</div>}
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={6}>
              <FormGroup>
                <Label>New Variant Value *</Label>
                <Input
                  type="number"
                  name="new_variant_value"
                  value={formData.new_variant_value}
                  onChange={handleInputChange}
                  min={formData.actionType === "downgrade" ? 0.01 : formData.current_variant_value}
                  max={formData.actionType === "upgrade" ? undefined : formData.current_variant_value}
                  step="0.01"
                  invalid={!!errors.new_variant_value}
                />
                {errors.new_variant_value ? (
                  <div className="text-danger small mt-1">{errors.new_variant_value}</div>
                ) : (
                  <small className="text-muted">
                    {formData.actionType === "upgrade"
                      ? `Must be greater than current value (${formData.current_variant_value})`
                      : `Must be less than current value (${formData.current_variant_value})`}
                  </small>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Current Remaining Days</Label>
                <Input
                  type="text"
                  value={formData.remaining_days}
                  readOnly
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={6}>
              <FormGroup>
                <Label>Adjusted Remaining Days</Label>
                <Input
                  type="text"
                  value={formData.calculated_remaining_days}
                  readOnly
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Per Day Amount</Label>
                <Input
                  type="text"
                  value={`$${subscription?.details?.[0]?.per_day_amount}`}
                  readOnly
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={6}>
              <FormGroup>
                <Label>Current End Date</Label>
                <Input
                  type="text"
                  value={moment(formData.end_date).format('MMM DD, YYYY')}
                  readOnly
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>New End Date</Label>
                <Input
                  type="text"
                  value={moment(formData.new_end_date).format('MMM DD, YYYY')}
                  readOnly
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup className="mt-3">
            <Label>Reason for {formData.actionType} *</Label>
            <Input
              type="textarea"
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              placeholder={`Please specify the reason for ${formData.actionType}`}
              invalid={!!errors.reason}
            />
            {errors.reason && <div className="text-danger small mt-1">{errors.reason}</div>}
          </FormGroup>

          <Alert color="info" className="mt-3">
            <i className="ri-information-line me-2"></i>
            {formData.actionType === "upgrade"
              ? `Upgrading will decrease your remaining days to ${formData.calculated_remaining_days} days and change your end date to ${moment(formData.new_end_date).format('MMM DD, YYYY')}.`
              : `Downgrading will extend your remaining days to ${formData.calculated_remaining_days} days and change your end date to ${moment(formData.new_end_date).format('MMM DD, YYYY')}.`}
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
          <Button color="primary" type="submit">
            Confirm {formData.actionType}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default UpgradeDowngradeModal;